/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import {DependencyList, useCallback, useEffect, useRef, useState} from 'react'

const usePromiseEffect = (call: () => Promise<any>, triggerWhenChanged: DependencyList) => {
  useEffect(() => {
    call()
  }, triggerWhenChanged)
}

const isLastPage = (items: any[], takeCount: number) => items.length < takeCount

type ListSearchOptions = {
  take?: number
  skip?: number
}

export function useAsyncList<TItem, TOptions extends ListSearchOptions>(listOptions: {
  defaultOptions: TOptions
  load: (options: TOptions) => Promise<TItem[]>
}) {
  const [options, setOptions] = useState(listOptions.defaultOptions)
  const isLoading = useRef(false)
  const [items, setItems] = useState([] as TItem[])
  const [hasMoreData, setHasMoreData] = useState(true)

  usePromiseEffect(async () => {
    isLoading.current = true
    setItems([])
    setHasMoreData(true)
    const response = await listOptions.load(options)
    setItems(response)
    setHasMoreData(!isLastPage(response, options.take!))
    isLoading.current = false
  }, [options])

  const loadMoreData = useCallback(async () => {
    if (!isLoading.current) {
      isLoading.current = true

      const nextPage = await listOptions.load({
        ...options,
        skip: items?.length
      })

      setItems(items => [...items, ...nextPage])

      setHasMoreData(!isLastPage(nextPage, options.take!))

      isLoading.current = false
    }
  }, [items, options])

  return {
    items,
    isLoading: isLoading.current,
    hasMoreData,
    loadMoreData,
    options,
    setOptions
  }
}
